import { StatusData } from "@fairfleet/geotab";
import { readingMs } from "./newestReading";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Fetches every reading of one series between two dates. */
export type FetchWindow = (fromDate: Date, toDate: Date) => Promise<StatusData[]>;

export interface FindLastReadingOptions {
  /** The first window, reaching back from `before`. */
  firstSpanMs?: number;
  /** How far back to look at all; older readings count as never reported. */
  maxAgeMs?: number;
  /** How much longer each window is than the one before. */
  growth?: number;
}

/**
 * The newest reading the vehicle really sent before a date, however long ago.
 *
 * Geotab interpolates odometers: a search with a date range always returns made-up rows at its
 * two edges, without an id, and refuses to sort newest first. So the windows reach backwards,
 * each twice as long as the one before, and only rows with an id count. A vehicle that reports
 * daily is answered by the first window; a year back takes nine.
 *
 * The made-up edges come for any series that has a reading at all, however far away. A window
 * without even those means the vehicle never sent this diagnostic, and the search stops there.
 */
export async function findLastReading(
  fetchWindow: FetchWindow,
  before: Date,
  { firstSpanMs = DAY_MS, maxAgeMs = 400 * DAY_MS, growth = 2 }: FindLastReadingOptions = {},
): Promise<StatusData | undefined> {
  const oldest = before.getTime() - maxAgeMs;
  let toMs = before.getTime();
  let spanMs = firstSpanMs;

  while (toMs > oldest) {
    const fromMs = toMs - spanMs;
    const rows = await fetchWindow(new Date(fromMs), new Date(toMs));
    if (rows.length === 0) {
      return undefined;
    }
    let newest: StatusData | undefined;
    for (const row of rows) {
      if (row.id && (!newest || readingMs(row.dateTime) > readingMs(newest.dateTime))) {
        newest = row;
      }
    }
    if (newest) {
      return newest;
    }
    toMs = fromMs;
    spanMs *= growth;
  }

  return undefined;
}
